import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Loader2, Wand2, ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { LabelElement } from '@/types/label';
import { sanitizeLabelElements } from '@/lib/templateSanitizer';

interface AiLabelCreatorProps {
  labelWidth: number;
  labelHeight: number;
  onApplyElements: (elements: LabelElement[], name?: string) => void;
}

const EXAMPLE_PROMPTS = [
  'Shipping label with sender, recipient, and barcode',
  'Product label with name, price, and QR code',
  'Name badge with company logo placeholder and name',
  'Inventory tag with item number, description, and barcode',
];

export const AiLabelCreator: React.FC<AiLabelCreatorProps> = ({
  labelWidth,
  labelHeight,
  onApplyElements,
}) => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ elements: LabelElement[]; name: string; description: string } | null>(null);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please describe the label you want to create');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke('ai-label-creator', {
        body: { prompt: prompt.trim(), currentTemplate: { width: labelWidth, height: labelHeight } },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.elements && Array.isArray(data.elements)) {
        const sanitized = sanitizeLabelElements(data.elements, labelWidth, labelHeight);

        if (sanitized.length === 0) {
          throw new Error('AI generated no valid elements. Try a different description.');
        }

        setResult({
          elements: sanitized,
          name: data.name || 'AI Generated Label',
          description: data.description || '',
        });
        toast.success('Label design generated!');
      } else {
        throw new Error('Invalid response from AI');
      }
    } catch (err: any) {
      console.error('AI label creation error:', err);
      toast.error(err.message || 'Failed to generate label');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (!result) return;
    onApplyElements(result.elements, result.name);
    toast.success('AI design applied to your label!');
    setResult(null);
    setPrompt('');
  };

  return (
    <div className="p-4 space-y-4 max-w-2xl mx-auto">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="w-5 h-5 text-primary" />
            AI Label Creator
          </CardTitle>
          <CardDescription>
            Describe the label you want and AI will generate the design for you.
            Current size: {labelWidth}mm × {labelHeight}mm
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            placeholder="Describe your label... e.g. 'A product label with a large title at the top, price on the right, and a barcode at the bottom'"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            className="min-h-[100px] resize-none"
            disabled={loading}
          />

          <div className="flex flex-wrap gap-1.5">
            {EXAMPLE_PROMPTS.map((example) => (
              <Badge
                key={example}
                variant="outline"
                className="cursor-pointer hover:bg-primary/10 transition-colors text-xs"
                onClick={() => setPrompt(example)}
              >
                {example}
              </Badge>
            ))}
          </div>

          <Button onClick={handleGenerate} disabled={loading || !prompt.trim()} className="w-full gap-2">
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4" />
                Generate Label Design
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {result && (
        <Card className="border-primary/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{result.name}</CardTitle>
            <CardDescription>{result.description}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-sm text-muted-foreground">
              <span className="font-medium">{result.elements.length}</span> elements generated
            </div>
            <div className="flex flex-wrap gap-1.5">
              {result.elements.map((el, i) => (
                <Badge key={i} variant="secondary" className="text-xs">
                  {el.type}: {el.content?.substring(0, 20) || '(empty)'}
                </Badge>
              ))}
            </div>
            <Button onClick={handleApply} className="w-full gap-2">
              <ArrowRight className="w-4 h-4" />
              Apply to Label
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
